import apiClient from './client';
import type {
  IntakeClient,
  IntakeDocumentType,
  IntakeClientDocument,
  IntakeChatMessage,
  IntakeChatSession,
  IntakeFaq,
  IntakeDashboardStats,
  ClientProgressSummary,
  CreateIntakeClientInput,
  UpdateIntakeClientInput,
  CreateDocumentTypeInput,
  UpdateDocumentTypeInput,
  ValidateDocumentInput,
  CreateFaqInput,
  UpdateFaqInput,
  ListIntakeClientsParams,
  ListDocumentTypesParams,
  ListClientDocumentsParams,
  ListFaqsParams,
} from '@/types/intake';

// Sample data for development
const SAMPLE_CLIENTS: IntakeClient[] = [
  {
    id: 'client-1',
    tenantId: 'tenant-1',
    name: 'John Smith',
    email: 'john.smith@example.com',
    phone: '+61 412 345 678',
    company: 'Smith & Associates Pty Ltd',
    abn: '12 345 678 901',
    status: 'active',
    intakeProgress: 65,
    forcePasswordChange: false,
    assignedTo: 'admin-1',
    riskLevel: 'low',
    riskScore: 25,
    lastLoginAt: '2026-01-10T14:30:00Z',
    createdAt: '2026-01-05T09:00:00Z',
    updatedAt: '2026-01-10T14:30:00Z',
  },
  {
    id: 'client-2',
    tenantId: 'tenant-1',
    name: 'Sarah Johnson',
    email: 'sarah.j@techinnovate.com.au',
    phone: '+61 423 456 789',
    company: 'Tech Innovate Solutions',
    abn: '23 456 789 012',
    status: 'submitted',
    intakeProgress: 100,
    forcePasswordChange: false,
    assignedTo: 'admin-1',
    riskLevel: 'medium',
    riskScore: 45,
    lastLoginAt: '2026-01-09T16:45:00Z',
    submittedAt: '2026-01-09T16:50:00Z',
    createdAt: '2026-01-03T11:00:00Z',
    updatedAt: '2026-01-09T16:50:00Z',
  },
  {
    id: 'client-3',
    tenantId: 'tenant-1',
    name: 'Michael Chen',
    email: 'mchen@globaltrading.net',
    phone: '+61 434 567 890',
    company: 'Global Trading Co',
    abn: '34 567 890 123',
    status: 'under_review',
    intakeProgress: 100,
    forcePasswordChange: false,
    riskLevel: 'high',
    riskScore: 72,
    notes: 'Complex business structure - requires additional verification',
    lastLoginAt: '2026-01-08T10:20:00Z',
    submittedAt: '2026-01-08T10:25:00Z',
    createdAt: '2026-01-02T08:30:00Z',
    updatedAt: '2026-01-08T10:25:00Z',
  },
  {
    id: 'client-4',
    tenantId: 'tenant-1',
    name: 'Emma Williams',
    email: 'emma@williamsconsulting.com.au',
    company: 'Williams Consulting',
    abn: '45 678 901 234',
    status: 'pending',
    intakeProgress: 0,
    forcePasswordChange: true,
    createdAt: '2026-01-10T15:00:00Z',
    updatedAt: '2026-01-10T15:00:00Z',
  },
  {
    id: 'client-5',
    tenantId: 'tenant-1',
    name: 'David Brown',
    email: 'dbrown@brownmanufacturing.com',
    phone: '+61 456 789 012',
    company: 'Brown Manufacturing Ltd',
    abn: '56 789 012 345',
    status: 'approved',
    intakeProgress: 100,
    forcePasswordChange: false,
    riskLevel: 'low',
    riskScore: 15,
    lastLoginAt: '2026-01-07T09:00:00Z',
    submittedAt: '2026-01-06T14:30:00Z',
    approvedAt: '2026-01-07T11:00:00Z',
    createdAt: '2025-12-20T10:00:00Z',
    updatedAt: '2026-01-07T11:00:00Z',
  },
  {
    id: 'client-6',
    tenantId: 'tenant-1',
    name: 'Lisa Taylor',
    email: 'lisa@taylordesigns.com.au',
    phone: '+61 467 890 123',
    company: 'Taylor Design Studio',
    abn: '67 890 123 456',
    status: 'rejected',
    intakeProgress: 85,
    forcePasswordChange: false,
    riskLevel: 'critical',
    riskScore: 92,
    notes: 'Missing critical documentation, potential compliance issues',
    lastLoginAt: '2026-01-05T13:00:00Z',
    submittedAt: '2026-01-05T13:10:00Z',
    createdAt: '2025-12-28T09:00:00Z',
    updatedAt: '2026-01-06T10:00:00Z',
  },
];

const SAMPLE_DOCUMENT_TYPES: IntakeDocumentType[] = [
  {
    id: 'doctype-1',
    tenantId: 'tenant-1',
    name: 'Photo ID',
    description: 'Valid government-issued photo identification (passport, drivers license)',
    category: 'identification',
    isRequired: true,
    isActive: true,
    displayOrder: 1,
    acceptedMimeTypes: ['image/jpeg', 'image/png', 'application/pdf'],
    maxFileSizeMb: 10,
    extractionFields: [
      { key: 'full_name', label: 'Full Name', type: 'text', required: true },
      { key: 'id_number', label: 'ID Number', type: 'text', required: true },
      { key: 'expiry_date', label: 'Expiry Date', type: 'date', required: true },
    ],
    createdAt: '2025-12-01T00:00:00Z',
    updatedAt: '2025-12-01T00:00:00Z',
  },
  {
    id: 'doctype-2',
    tenantId: 'tenant-1',
    name: 'Bank Statement',
    description: 'Recent bank statement (within last 3 months)',
    category: 'financial',
    isRequired: true,
    isActive: true,
    displayOrder: 2,
    acceptedMimeTypes: ['application/pdf'],
    maxFileSizeMb: 20,
    validationRules: {
      requireDate: true,
      minPages: 1,
    },
    extractionFields: [
      { key: 'account_name', label: 'Account Name', type: 'text', required: true },
      { key: 'bsb', label: 'BSB', type: 'text', required: true },
      { key: 'account_number', label: 'Account Number', type: 'text', required: true },
      { key: 'statement_date', label: 'Statement Date', type: 'date', required: true },
      { key: 'closing_balance', label: 'Closing Balance', type: 'currency', required: false },
    ],
    createdAt: '2025-12-01T00:00:00Z',
    updatedAt: '2025-12-01T00:00:00Z',
  },
  {
    id: 'doctype-3',
    tenantId: 'tenant-1',
    name: 'Tax Return',
    description: 'Most recent individual or business tax return',
    category: 'tax',
    isRequired: true,
    isActive: true,
    displayOrder: 3,
    acceptedMimeTypes: ['application/pdf'],
    maxFileSizeMb: 50,
    extractionFields: [
      { key: 'tax_year', label: 'Tax Year', type: 'text', required: true },
      { key: 'total_income', label: 'Total Income', type: 'currency', required: true },
      { key: 'tax_payable', label: 'Tax Payable', type: 'currency', required: true },
      { key: 'tfn', label: 'Tax File Number', type: 'text', required: false },
    ],
    createdAt: '2025-12-01T00:00:00Z',
    updatedAt: '2025-12-01T00:00:00Z',
  },
  {
    id: 'doctype-4',
    tenantId: 'tenant-1',
    name: 'ABN Registration',
    description: 'Australian Business Number registration certificate',
    category: 'compliance',
    isRequired: true,
    isActive: true,
    displayOrder: 4,
    acceptedMimeTypes: ['application/pdf', 'image/jpeg', 'image/png'],
    maxFileSizeMb: 10,
    validationRules: {
      requireAbn: true,
    },
    extractionFields: [
      { key: 'abn', label: 'ABN', type: 'text', required: true },
      { key: 'business_name', label: 'Business Name', type: 'text', required: true },
      { key: 'gst_registered', label: 'GST Registered', type: 'boolean', required: true },
      { key: 'registration_date', label: 'Registration Date', type: 'date', required: false },
    ],
    createdAt: '2025-12-01T00:00:00Z',
    updatedAt: '2025-12-01T00:00:00Z',
  },
  {
    id: 'doctype-5',
    tenantId: 'tenant-1',
    name: 'BAS Statement',
    description: 'Business Activity Statement (most recent quarter)',
    category: 'tax',
    isRequired: false,
    isActive: true,
    displayOrder: 5,
    acceptedMimeTypes: ['application/pdf'],
    maxFileSizeMb: 20,
    extractionFields: [
      { key: 'period', label: 'BAS Period', type: 'text', required: true },
      { key: 'gst_collected', label: 'GST Collected', type: 'currency', required: true },
      { key: 'gst_paid', label: 'GST Paid', type: 'currency', required: true },
      { key: 'payg_withheld', label: 'PAYG Withheld', type: 'currency', required: false },
    ],
    createdAt: '2025-12-01T00:00:00Z',
    updatedAt: '2025-12-01T00:00:00Z',
  },
  {
    id: 'doctype-6',
    tenantId: 'tenant-1',
    name: 'Proof of Address',
    description: 'Utility bill or official document showing current address',
    category: 'identification',
    isRequired: false,
    isActive: true,
    displayOrder: 6,
    acceptedMimeTypes: ['application/pdf', 'image/jpeg', 'image/png'],
    maxFileSizeMb: 10,
    extractionFields: [
      { key: 'address', label: 'Address', type: 'text', required: true },
      { key: 'document_date', label: 'Document Date', type: 'date', required: true },
    ],
    createdAt: '2025-12-01T00:00:00Z',
    updatedAt: '2025-12-01T00:00:00Z',
  },
];

const SAMPLE_CLIENT_DOCUMENTS: IntakeClientDocument[] = [
  // Client 1 documents
  {
    id: 'doc-1',
    clientId: 'client-1',
    documentTypeId: 'doctype-1',
    fileName: 'john_smith_passport.pdf',
    originalName: 'passport_scan.pdf',
    mimeType: 'application/pdf',
    fileSizeBytes: 1245678,
    s3Key: 'intake/client-1/doc-1.pdf',
    status: 'approved',
    extractedData: {
      full_name: 'John Michael Smith',
      id_number: 'PA1234567',
      expiry_date: '2028-05-15',
    },
    extractionConfidence: 0.95,
    uploadedAt: '2026-01-06T10:00:00Z',
    processedAt: '2026-01-06T10:05:00Z',
    validatedAt: '2026-01-06T14:00:00Z',
    validatedBy: 'admin-1',
  },
  {
    id: 'doc-2',
    clientId: 'client-1',
    documentTypeId: 'doctype-2',
    fileName: 'john_smith_bank_statement.pdf',
    originalName: 'bank_statement_dec2025.pdf',
    mimeType: 'application/pdf',
    fileSizeBytes: 2345678,
    s3Key: 'intake/client-1/doc-2.pdf',
    status: 'extracted',
    extractedData: {
      account_name: 'John Smith',
      bsb: '062-000',
      account_number: '12345678',
      statement_date: '2025-12-31',
      closing_balance: 45678.90,
    },
    extractionConfidence: 0.88,
    uploadedAt: '2026-01-07T11:00:00Z',
    processedAt: '2026-01-07T11:10:00Z',
  },
  {
    id: 'doc-3',
    clientId: 'client-1',
    documentTypeId: 'doctype-3',
    fileName: 'john_smith_tax_return.pdf',
    originalName: 'ITR_2024-25.pdf',
    mimeType: 'application/pdf',
    fileSizeBytes: 5678901,
    s3Key: 'intake/client-1/doc-3.pdf',
    status: 'uploaded',
    uploadedAt: '2026-01-10T09:00:00Z',
  },
  // Client 2 documents (all approved)
  {
    id: 'doc-4',
    clientId: 'client-2',
    documentTypeId: 'doctype-1',
    fileName: 'sarah_johnson_license.pdf',
    originalName: 'drivers_license.pdf',
    mimeType: 'application/pdf',
    fileSizeBytes: 987654,
    s3Key: 'intake/client-2/doc-4.pdf',
    status: 'approved',
    extractedData: {
      full_name: 'Sarah Anne Johnson',
      id_number: 'DL98765432',
      expiry_date: '2027-08-20',
    },
    extractionConfidence: 0.97,
    uploadedAt: '2026-01-04T14:00:00Z',
    processedAt: '2026-01-04T14:05:00Z',
    validatedAt: '2026-01-05T09:00:00Z',
    validatedBy: 'admin-1',
  },
  {
    id: 'doc-5',
    clientId: 'client-2',
    documentTypeId: 'doctype-2',
    fileName: 'sarah_johnson_bank.pdf',
    originalName: 'statement.pdf',
    mimeType: 'application/pdf',
    fileSizeBytes: 1876543,
    s3Key: 'intake/client-2/doc-5.pdf',
    status: 'approved',
    extractedData: {
      account_name: 'Tech Innovate Solutions',
      bsb: '083-001',
      account_number: '87654321',
      statement_date: '2025-12-15',
      closing_balance: 125000.00,
    },
    extractionConfidence: 0.92,
    uploadedAt: '2026-01-05T10:00:00Z',
    processedAt: '2026-01-05T10:08:00Z',
    validatedAt: '2026-01-06T11:00:00Z',
    validatedBy: 'admin-1',
  },
  // Client 3 documents (under review)
  {
    id: 'doc-6',
    clientId: 'client-3',
    documentTypeId: 'doctype-1',
    fileName: 'michael_chen_passport.pdf',
    originalName: 'passport.pdf',
    mimeType: 'application/pdf',
    fileSizeBytes: 1123456,
    s3Key: 'intake/client-3/doc-6.pdf',
    status: 'validated',
    extractedData: {
      full_name: 'Michael Wei Chen',
      id_number: 'E12345678',
      expiry_date: '2029-03-10',
    },
    extractionConfidence: 0.94,
    adminNotes: 'Verified against secondary ID',
    uploadedAt: '2026-01-03T09:00:00Z',
    processedAt: '2026-01-03T09:05:00Z',
    validatedAt: '2026-01-04T10:00:00Z',
    validatedBy: 'admin-1',
  },
  {
    id: 'doc-7',
    clientId: 'client-3',
    documentTypeId: 'doctype-4',
    fileName: 'global_trading_abn.pdf',
    originalName: 'abn_certificate.pdf',
    mimeType: 'application/pdf',
    fileSizeBytes: 456789,
    s3Key: 'intake/client-3/doc-7.pdf',
    status: 'extracted',
    extractedData: {
      abn: '34 567 890 123',
      business_name: 'Global Trading Co Pty Ltd',
      gst_registered: true,
      registration_date: '2018-06-15',
    },
    extractionConfidence: 0.99,
    uploadedAt: '2026-01-03T09:30:00Z',
    processedAt: '2026-01-03T09:35:00Z',
  },
];

const SAMPLE_FAQS: IntakeFaq[] = [
  {
    id: 'faq-1',
    tenantId: 'tenant-1',
    question: 'What documents do I need to provide for client onboarding?',
    answer: 'For standard onboarding, you will need to provide: 1) A valid photo ID (passport or drivers license), 2) Recent bank statement (within 3 months), 3) Most recent tax return, and 4) ABN registration certificate. Additional documents may be required based on your business type.',
    category: 'Documents',
    isActive: true,
    displayOrder: 1,
    createdAt: '2025-12-01T00:00:00Z',
    updatedAt: '2025-12-01T00:00:00Z',
  },
  {
    id: 'faq-2',
    tenantId: 'tenant-1',
    question: 'How long does the intake process take?',
    answer: 'The typical intake process takes 2-5 business days once all documents are submitted. Processing time may vary depending on the complexity of your business structure and document verification requirements.',
    category: 'Process',
    isActive: true,
    displayOrder: 2,
    createdAt: '2025-12-01T00:00:00Z',
    updatedAt: '2025-12-01T00:00:00Z',
  },
  {
    id: 'faq-3',
    tenantId: 'tenant-1',
    question: 'What file formats are accepted for document uploads?',
    answer: 'We accept PDF, JPEG, and PNG file formats. For best results, we recommend uploading documents as PDFs. Maximum file size is 50MB per document.',
    category: 'Documents',
    isActive: true,
    displayOrder: 3,
    createdAt: '2025-12-01T00:00:00Z',
    updatedAt: '2025-12-01T00:00:00Z',
  },
  {
    id: 'faq-4',
    tenantId: 'tenant-1',
    question: 'Can I update my documents after submission?',
    answer: 'Yes, you can re-upload documents if they were rejected or if you need to provide updated versions. Simply go to your dashboard, find the document, and click "Re-upload".',
    category: 'Documents',
    isActive: true,
    displayOrder: 4,
    createdAt: '2025-12-01T00:00:00Z',
    updatedAt: '2025-12-01T00:00:00Z',
  },
  {
    id: 'faq-5',
    tenantId: 'tenant-1',
    question: 'How is my data protected?',
    answer: 'All documents and personal information are encrypted both in transit and at rest using industry-standard AES-256 encryption. We comply with Australian Privacy Principles and maintain strict data handling procedures.',
    category: 'Security',
    isActive: true,
    displayOrder: 5,
    createdAt: '2025-12-01T00:00:00Z',
    updatedAt: '2025-12-01T00:00:00Z',
  },
];

const SAMPLE_CHAT_MESSAGES: IntakeChatMessage[] = [
  {
    id: 'msg-1',
    clientId: 'client-1',
    sessionId: 'session-1',
    role: 'user',
    content: 'What documents do I still need to upload?',
    createdAt: '2026-01-10T10:00:00Z',
  },
  {
    id: 'msg-2',
    clientId: 'client-1',
    sessionId: 'session-1',
    role: 'assistant',
    content: 'Based on your current progress, you still need to upload your Tax Return. You have already successfully uploaded your Photo ID and Bank Statement. Would you like me to guide you through the tax return upload process?',
    metadata: {
      confidence: 0.95,
      suggestedActions: ['Upload Tax Return', 'View Requirements'],
    },
    createdAt: '2026-01-10T10:00:05Z',
  },
  {
    id: 'msg-3',
    clientId: 'client-1',
    sessionId: 'session-1',
    role: 'user',
    content: 'What format should my tax return be in?',
    createdAt: '2026-01-10T10:01:00Z',
  },
  {
    id: 'msg-4',
    clientId: 'client-1',
    sessionId: 'session-1',
    role: 'assistant',
    content: 'Your tax return should be uploaded as a PDF file. The maximum file size is 50MB. Please ensure it\'s the most recent tax return (2024-25 financial year). If you downloaded it from myGov, it should already be in the correct format.',
    metadata: {
      sourceDocuments: ['faq-3'],
      confidence: 0.98,
    },
    createdAt: '2026-01-10T10:01:05Z',
  },
];

// Simulate API delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Use sample data flag - set to false when real API is available
const USE_SAMPLE_DATA = false;

class IntakeService {
  // ==================== CLIENT METHODS ====================

  async listClients(params: ListIntakeClientsParams = {}): Promise<{ clients: IntakeClient[]; total: number }> {
    if (USE_SAMPLE_DATA) {
      await delay(300);
      let filtered = [...SAMPLE_CLIENTS];

      if (params.search) {
        const search = params.search.toLowerCase();
        filtered = filtered.filter(c =>
          c.name.toLowerCase().includes(search) ||
          c.email.toLowerCase().includes(search) ||
          c.company?.toLowerCase().includes(search)
        );
      }

      if (params.status) {
        filtered = filtered.filter(c => c.status === params.status);
      }

      if (params.riskLevel) {
        filtered = filtered.filter(c => c.riskLevel === params.riskLevel);
      }

      // Sort
      if (params.sortBy) {
        filtered.sort((a, b) => {
          const aVal = a[params.sortBy as keyof IntakeClient] as string;
          const bVal = b[params.sortBy as keyof IntakeClient] as string;
          const order = params.sortOrder === 'desc' ? -1 : 1;
          return aVal > bVal ? order : -order;
        });
      }

      return { clients: filtered, total: filtered.length };
    }

    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, String(value));
    });

    const response = await apiClient.get(`/intake/clients?${queryParams.toString()}`);
    return response.data;
  }

  async getClient(id: string): Promise<IntakeClient> {
    if (USE_SAMPLE_DATA) {
      await delay(200);
      const client = SAMPLE_CLIENTS.find(c => c.id === id);
      if (!client) throw new Error('Client not found');
      return client;
    }

    const response = await apiClient.get(`/intake/clients/${id}`);
    return response.data;
  }

  async createClient(input: CreateIntakeClientInput): Promise<IntakeClient> {
    if (USE_SAMPLE_DATA) {
      await delay(500);
      const newClient: IntakeClient = {
        id: `client-${Date.now()}`,
        tenantId: 'tenant-1',
        ...input,
        status: 'pending',
        intakeProgress: 0,
        forcePasswordChange: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      SAMPLE_CLIENTS.push(newClient);
      return newClient;
    }

    const response = await apiClient.post('/intake/clients', input);
    return response.data;
  }

  async updateClient(id: string, input: UpdateIntakeClientInput): Promise<IntakeClient> {
    if (USE_SAMPLE_DATA) {
      await delay(300);
      const index = SAMPLE_CLIENTS.findIndex(c => c.id === id);
      if (index === -1) throw new Error('Client not found');
      SAMPLE_CLIENTS[index] = { ...SAMPLE_CLIENTS[index], ...input, updatedAt: new Date().toISOString() };
      return SAMPLE_CLIENTS[index];
    }

    const response = await apiClient.put(`/intake/clients/${id}`, input);
    return response.data;
  }

  async deleteClient(id: string): Promise<void> {
    if (USE_SAMPLE_DATA) {
      await delay(300);
      const index = SAMPLE_CLIENTS.findIndex(c => c.id === id);
      if (index !== -1) SAMPLE_CLIENTS.splice(index, 1);
      return;
    }

    await apiClient.delete(`/intake/clients/${id}`);
  }

  async getClientProgress(clientId: string): Promise<ClientProgressSummary> {
    if (USE_SAMPLE_DATA) {
      await delay(200);
      const docs = SAMPLE_CLIENT_DOCUMENTS.filter(d => d.clientId === clientId);
      const requiredTypes = SAMPLE_DOCUMENT_TYPES.filter(t => t.isRequired);

      return {
        totalRequired: requiredTypes.length,
        uploaded: docs.length,
        approved: docs.filter(d => d.status === 'approved').length,
        rejected: docs.filter(d => d.status === 'rejected').length,
        pending: requiredTypes.length - docs.length,
        progressPercent: Math.round((docs.length / requiredTypes.length) * 100),
      };
    }

    const response = await apiClient.get(`/intake/clients/${clientId}/progress`);
    // Ensure valid progress structure
    if (response.data && typeof response.data.totalRequired === 'number') {
      return response.data;
    }
    return {
      totalRequired: 0,
      uploaded: 0,
      approved: 0,
      rejected: 0,
      pending: 0,
      progressPercent: 0,
    };
  }

  async resendWelcomeEmail(clientId: string): Promise<void> {
    if (USE_SAMPLE_DATA) {
      await delay(500);
      return;
    }

    await apiClient.post(`/intake/clients/${clientId}/resend-welcome`);
  }

  // ==================== DOCUMENT TYPE METHODS ====================

  async listDocumentTypes(params: ListDocumentTypesParams = {}): Promise<{ documentTypes: IntakeDocumentType[]; total: number }> {
    if (USE_SAMPLE_DATA) {
      await delay(200);
      let filtered = [...SAMPLE_DOCUMENT_TYPES];

      if (params.category) {
        filtered = filtered.filter(d => d.category === params.category);
      }

      if (params.isRequired !== undefined) {
        filtered = filtered.filter(d => d.isRequired === params.isRequired);
      }

      if (params.isActive !== undefined) {
        filtered = filtered.filter(d => d.isActive === params.isActive);
      }

      filtered.sort((a, b) => a.displayOrder - b.displayOrder);

      return { documentTypes: filtered, total: filtered.length };
    }

    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, String(value));
    });

    const response = await apiClient.get(`/intake/document-types?${queryParams.toString()}`);
    // Ensure proper structure
    if (response.data && Array.isArray(response.data.documentTypes)) {
      return response.data;
    }
    return { documentTypes: [], total: 0 };
  }

  async getDocumentType(id: string): Promise<IntakeDocumentType> {
    if (USE_SAMPLE_DATA) {
      await delay(150);
      const docType = SAMPLE_DOCUMENT_TYPES.find(d => d.id === id);
      if (!docType) throw new Error('Document type not found');
      return docType;
    }

    const response = await apiClient.get(`/intake/document-types/${id}`);
    return response.data;
  }

  async createDocumentType(input: CreateDocumentTypeInput): Promise<IntakeDocumentType> {
    if (USE_SAMPLE_DATA) {
      await delay(400);
      const newDocType: IntakeDocumentType = {
        id: `doctype-${Date.now()}`,
        tenantId: 'tenant-1',
        ...input,
        isActive: true,
        displayOrder: input.displayOrder || SAMPLE_DOCUMENT_TYPES.length + 1,
        acceptedMimeTypes: input.acceptedMimeTypes || ['application/pdf'],
        maxFileSizeMb: input.maxFileSizeMb || 20,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      SAMPLE_DOCUMENT_TYPES.push(newDocType);
      return newDocType;
    }

    const response = await apiClient.post('/intake/document-types', input);
    return response.data;
  }

  async updateDocumentType(id: string, input: UpdateDocumentTypeInput): Promise<IntakeDocumentType> {
    if (USE_SAMPLE_DATA) {
      await delay(300);
      const index = SAMPLE_DOCUMENT_TYPES.findIndex(d => d.id === id);
      if (index === -1) throw new Error('Document type not found');
      SAMPLE_DOCUMENT_TYPES[index] = { ...SAMPLE_DOCUMENT_TYPES[index], ...input, updatedAt: new Date().toISOString() };
      return SAMPLE_DOCUMENT_TYPES[index];
    }

    const response = await apiClient.put(`/intake/document-types/${id}`, input);
    return response.data;
  }

  async deleteDocumentType(id: string): Promise<void> {
    if (USE_SAMPLE_DATA) {
      await delay(300);
      const index = SAMPLE_DOCUMENT_TYPES.findIndex(d => d.id === id);
      if (index !== -1) SAMPLE_DOCUMENT_TYPES.splice(index, 1);
      return;
    }

    await apiClient.delete(`/intake/document-types/${id}`);
  }

  // ==================== CLIENT DOCUMENT METHODS ====================

  async listClientDocuments(params: ListClientDocumentsParams): Promise<IntakeClientDocument[]> {
    if (USE_SAMPLE_DATA) {
      await delay(250);
      let filtered = SAMPLE_CLIENT_DOCUMENTS.filter(d => d.clientId === params.clientId);

      if (params.status) {
        filtered = filtered.filter(d => d.status === params.status);
      }

      // Attach document type info
      return filtered.map(doc => ({
        ...doc,
        documentType: SAMPLE_DOCUMENT_TYPES.find(dt => dt.id === doc.documentTypeId),
      }));
    }

    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);

    const response = await apiClient.get(`/intake/clients/${params.clientId}/documents?${queryParams.toString()}`);
    // Ensure we always return an array
    return Array.isArray(response.data) ? response.data : [];
  }

  async getClientDocument(clientId: string, documentId: string): Promise<IntakeClientDocument> {
    if (USE_SAMPLE_DATA) {
      await delay(150);
      const doc = SAMPLE_CLIENT_DOCUMENTS.find(d => d.id === documentId && d.clientId === clientId);
      if (!doc) throw new Error('Document not found');
      return {
        ...doc,
        documentType: SAMPLE_DOCUMENT_TYPES.find(dt => dt.id === doc.documentTypeId),
      };
    }

    const response = await apiClient.get(`/intake/clients/${clientId}/documents/${documentId}`);
    return response.data;
  }

  async validateDocument(input: ValidateDocumentInput): Promise<IntakeClientDocument> {
    if (USE_SAMPLE_DATA) {
      await delay(400);
      const index = SAMPLE_CLIENT_DOCUMENTS.findIndex(d => d.id === input.documentId);
      if (index === -1) throw new Error('Document not found');

      SAMPLE_CLIENT_DOCUMENTS[index] = {
        ...SAMPLE_CLIENT_DOCUMENTS[index],
        status: input.action === 'approve' ? 'approved' : 'rejected',
        adminNotes: input.adminNotes,
        rejectionReason: input.rejectionReason,
        validatedAt: new Date().toISOString(),
        validatedBy: 'admin-1',
      };

      return SAMPLE_CLIENT_DOCUMENTS[index];
    }

    const response = await apiClient.post(`/intake/documents/${input.documentId}/validate`, input);
    return response.data;
  }

  async getDocumentDownloadUrl(documentId: string): Promise<{
    downloadUrl: string;
    fileName: string;
    mimeType: string;
    fileSizeBytes: number;
    uploadedAt: string;
  }> {
    if (USE_SAMPLE_DATA) {
      await delay(200);
      return {
        downloadUrl: `https://example-bucket.s3.amazonaws.com/download?signature=xxx`,
        fileName: 'sample-document.pdf',
        mimeType: 'application/pdf',
        fileSizeBytes: 1234567,
        uploadedAt: new Date().toISOString(),
      };
    }

    const response = await apiClient.get(`/intake/documents/${documentId}/download-url`);
    return response.data;
  }

  // Extract data from document using AWS Textract
  async extractDocument(documentId: string): Promise<{
    success: boolean;
    documentId: string;
    extractedData: {
      type: string;
      fields?: Record<string, string>;
      text?: string;
      keyValuePairs?: Record<string, string>;
    };
    confidence: number;
  }> {
    if (USE_SAMPLE_DATA) {
      await delay(1500); // Simulate extraction time
      return {
        success: true,
        documentId,
        extractedData: {
          type: 'drivers_license',
          fields: {
            firstName: 'John',
            lastName: 'Smith',
            dateOfBirth: '1985-03-15',
            licenseNumber: 'DL123456789',
            expiryDate: '2027-03-15',
            address: '123 Main Street, Sydney NSW 2000',
            licenseClass: 'C',
          },
        },
        confidence: 0.95,
      };
    }

    const response = await apiClient.post(`/intake/documents/${documentId}/extract`);
    return response.data;
  }

  // Get extracted data for a document
  async getExtractedData(documentId: string): Promise<{
    documentId: string;
    status: string;
    extractedData: any;
    confidence: number | null;
    error: string | null;
    processedAt: string | null;
  }> {
    if (USE_SAMPLE_DATA) {
      await delay(200);
      return {
        documentId,
        status: 'extracted',
        extractedData: {
          type: 'drivers_license',
          fields: {
            firstName: 'John',
            lastName: 'Smith',
            dateOfBirth: '1985-03-15',
            licenseNumber: 'DL123456789',
          },
        },
        confidence: 0.95,
        error: null,
        processedAt: new Date().toISOString(),
      };
    }

    const response = await apiClient.get(`/intake/documents/${documentId}/extracted-data`);
    return response.data;
  }

  async updateExtractedData(documentId: string, extractedData: any): Promise<{ success: boolean }> {
    if (USE_SAMPLE_DATA) {
      await delay(300);
      return { success: true };
    }

    const response = await apiClient.put(`/intake/documents/${documentId}/extracted-data`, {
      extractedData,
    });
    return response.data;
  }

  async getDocumentUploadUrl(clientId: string, documentTypeId: string, fileName: string): Promise<{ uploadUrl: string; s3Key: string }> {
    if (USE_SAMPLE_DATA) {
      await delay(200);
      return {
        uploadUrl: `https://example-bucket.s3.amazonaws.com/upload?signature=xxx`,
        s3Key: `intake/${clientId}/${documentTypeId}/${fileName}`,
      };
    }

    const response = await apiClient.post(`/intake/clients/${clientId}/documents/upload-url`, {
      documentTypeId,
      fileName,
    });
    return response.data;
  }

  // Get client's document requirements (what documents they need to submit)
  async getClientDocuments(clientId: string): Promise<{ documents: Array<{ documentTypeId: string; isRequired: boolean }> }> {
    if (USE_SAMPLE_DATA) {
      await delay(200);
      // Return sample document requirements
      return {
        documents: SAMPLE_DOCUMENT_TYPES.slice(0, 3).map((dt, i) => ({
          documentTypeId: dt.id,
          isRequired: i < 2, // First 2 are required
        })),
      };
    }

    const response = await apiClient.get(`/intake/clients/${clientId}/document-requirements`);
    return response.data;
  }

  // Set client's document requirements
  async setClientDocumentRequirements(
    clientId: string,
    requirements: Array<{ documentTypeId: string; isRequired: boolean }>
  ): Promise<void> {
    if (USE_SAMPLE_DATA) {
      await delay(300);
      console.log('Setting document requirements for client:', clientId, requirements);
      return;
    }

    await apiClient.put(`/intake/clients/${clientId}/document-requirements`, { requirements });
  }

  // ==================== DASHBOARD & STATS ====================

  async getDashboardStats(): Promise<IntakeDashboardStats> {
    if (USE_SAMPLE_DATA) {
      await delay(300);
      return {
        totalClients: SAMPLE_CLIENTS.length,
        pendingClients: SAMPLE_CLIENTS.filter(c => c.status === 'pending').length,
        activeClients: SAMPLE_CLIENTS.filter(c => c.status === 'active').length,
        submittedClients: SAMPLE_CLIENTS.filter(c => c.status === 'submitted').length,
        underReviewClients: SAMPLE_CLIENTS.filter(c => c.status === 'under_review').length,
        approvedClients: SAMPLE_CLIENTS.filter(c => c.status === 'approved').length,
        rejectedClients: SAMPLE_CLIENTS.filter(c => c.status === 'rejected').length,
        totalDocuments: SAMPLE_CLIENT_DOCUMENTS.length,
        pendingDocuments: SAMPLE_CLIENT_DOCUMENTS.filter(d => d.status === 'pending' || d.status === 'uploaded').length,
        uploadedDocuments: SAMPLE_CLIENT_DOCUMENTS.filter(d => ['uploaded', 'processing', 'extracted'].includes(d.status)).length,
        approvedDocuments: SAMPLE_CLIENT_DOCUMENTS.filter(d => d.status === 'approved').length,
        rejectedDocuments: SAMPLE_CLIENT_DOCUMENTS.filter(d => d.status === 'rejected').length,
        avgCompletionRate: 72,
        avgProcessingTime: 48,
        riskDistribution: {
          low: SAMPLE_CLIENTS.filter(c => c.riskLevel === 'low').length,
          medium: SAMPLE_CLIENTS.filter(c => c.riskLevel === 'medium').length,
          high: SAMPLE_CLIENTS.filter(c => c.riskLevel === 'high').length,
          critical: SAMPLE_CLIENTS.filter(c => c.riskLevel === 'critical').length,
        },
      };
    }

    const response = await apiClient.get('/intake/dashboard/stats');
    return response.data;
  }

  // ==================== FAQ METHODS ====================

  async listFaqs(params: ListFaqsParams = {}): Promise<{ faqs: IntakeFaq[]; total: number }> {
    if (USE_SAMPLE_DATA) {
      await delay(200);
      let filtered = [...SAMPLE_FAQS];

      if (params.category) {
        filtered = filtered.filter(f => f.category === params.category);
      }

      if (params.isActive !== undefined) {
        filtered = filtered.filter(f => f.isActive === params.isActive);
      }

      if (params.search) {
        const search = params.search.toLowerCase();
        filtered = filtered.filter(f =>
          f.question.toLowerCase().includes(search) ||
          f.answer.toLowerCase().includes(search)
        );
      }

      filtered.sort((a, b) => a.displayOrder - b.displayOrder);

      return { faqs: filtered, total: filtered.length };
    }

    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, String(value));
    });

    const response = await apiClient.get(`/intake/faqs?${queryParams.toString()}`);
    return response.data;
  }

  async createFaq(input: CreateFaqInput): Promise<IntakeFaq> {
    if (USE_SAMPLE_DATA) {
      await delay(400);
      const newFaq: IntakeFaq = {
        id: `faq-${Date.now()}`,
        tenantId: 'tenant-1',
        ...input,
        isActive: true,
        displayOrder: input.displayOrder || SAMPLE_FAQS.length + 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      SAMPLE_FAQS.push(newFaq);
      return newFaq;
    }

    const response = await apiClient.post('/intake/faqs', input);
    return response.data;
  }

  async updateFaq(id: string, input: UpdateFaqInput): Promise<IntakeFaq> {
    if (USE_SAMPLE_DATA) {
      await delay(300);
      const index = SAMPLE_FAQS.findIndex(f => f.id === id);
      if (index === -1) throw new Error('FAQ not found');
      SAMPLE_FAQS[index] = { ...SAMPLE_FAQS[index], ...input, updatedAt: new Date().toISOString() };
      return SAMPLE_FAQS[index];
    }

    const response = await apiClient.put(`/intake/faqs/${id}`, input);
    return response.data;
  }

  async deleteFaq(id: string): Promise<void> {
    if (USE_SAMPLE_DATA) {
      await delay(300);
      const index = SAMPLE_FAQS.findIndex(f => f.id === id);
      if (index !== -1) SAMPLE_FAQS.splice(index, 1);
      return;
    }

    await apiClient.delete(`/intake/faqs/${id}`);
  }

  // ==================== CHAT METHODS ====================

  async getChatMessages(clientId: string, sessionId?: string): Promise<IntakeChatMessage[]> {
    if (USE_SAMPLE_DATA) {
      await delay(200);
      let messages = SAMPLE_CHAT_MESSAGES.filter(m => m.clientId === clientId);
      if (sessionId) {
        messages = messages.filter(m => m.sessionId === sessionId);
      }
      return messages;
    }

    const queryParams = sessionId ? `?sessionId=${sessionId}` : '';
    const response = await apiClient.get(`/intake/clients/${clientId}/chat${queryParams}`);
    return response.data;
  }

  async sendChatMessage(clientId: string, content: string): Promise<IntakeChatMessage> {
    if (USE_SAMPLE_DATA) {
      await delay(800);

      // Create user message
      const userMessage: IntakeChatMessage = {
        id: `msg-${Date.now()}`,
        clientId,
        sessionId: 'session-1',
        role: 'user',
        content,
        createdAt: new Date().toISOString(),
      };
      SAMPLE_CHAT_MESSAGES.push(userMessage);

      // Simulate AI response
      const aiMessage: IntakeChatMessage = {
        id: `msg-${Date.now() + 1}`,
        clientId,
        sessionId: 'session-1',
        role: 'assistant',
        content: `Thank you for your question. Based on our knowledge base, I can help you with that. ${content.includes('document') ? 'For document-related queries, please ensure you have uploaded all required documents in the correct format (PDF preferred).' : 'Please let me know if you need any further assistance with your intake process.'}`,
        metadata: {
          confidence: 0.85,
          suggestedActions: ['View Documents', 'Contact Support'],
        },
        createdAt: new Date().toISOString(),
      };
      SAMPLE_CHAT_MESSAGES.push(aiMessage);

      return aiMessage;
    }

    const response = await apiClient.post(`/intake/clients/${clientId}/chat`, { content });
    return response.data;
  }

  // ==================== SYSTEM DOCUMENT TYPES (Super Admin) ====================

  async listSystemDocumentTypes(params: {
    category?: string;
    status?: string;
    isActive?: boolean;
  } = {}): Promise<{ documentTypes: any[]; total: number }> {
    if (USE_SAMPLE_DATA) {
      await delay(200);
      return { documentTypes: [], total: 0 };
    }

    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, String(value));
    });

    const response = await apiClient.get(`/admin/system-document-types?${queryParams.toString()}`);
    return response.data;
  }

  async getSystemDocumentType(id: string): Promise<any> {
    if (USE_SAMPLE_DATA) {
      await delay(150);
      return null;
    }

    const response = await apiClient.get(`/admin/system-document-types/${id}`);
    return response.data;
  }

  async createSystemDocumentType(input: any): Promise<any> {
    if (USE_SAMPLE_DATA) {
      await delay(400);
      return { id: `system-doctype-${Date.now()}`, ...input };
    }

    const response = await apiClient.post('/admin/system-document-types', input);
    return response.data;
  }

  async updateSystemDocumentType(id: string, input: any): Promise<any> {
    if (USE_SAMPLE_DATA) {
      await delay(300);
      return { id, ...input };
    }

    const response = await apiClient.put(`/admin/system-document-types/${id}`, input);
    return response.data;
  }

  async deleteSystemDocumentType(id: string): Promise<void> {
    if (USE_SAMPLE_DATA) {
      await delay(300);
      return;
    }

    await apiClient.delete(`/admin/system-document-types/${id}`);
  }

  // Copy system document type to tenant
  async copySystemDocumentTypeToTenant(systemDocTypeId: string): Promise<IntakeDocumentType> {
    if (USE_SAMPLE_DATA) {
      await delay(400);
      return {
        id: `doctype-${Date.now()}`,
        tenantId: 'tenant-1',
        systemDocumentTypeId: systemDocTypeId,
        name: 'Copied Document Type',
        category: 'other',
        isRequired: true,
        isActive: true,
        displayOrder: 0,
        acceptedMimeTypes: ['application/pdf'],
        maxFileSizeMb: 10,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    }

    const response = await apiClient.post(`/intake/document-types/copy-from-system/${systemDocTypeId}`);
    return response.data;
  }

  // Restore document type to system defaults
  async restoreDocumentTypeDefaults(docTypeId: string): Promise<IntakeDocumentType> {
    if (USE_SAMPLE_DATA) {
      await delay(300);
      throw new Error('Not available in sample mode');
    }

    const response = await apiClient.post(`/intake/document-types/${docTypeId}/restore-defaults`);
    return response.data;
  }
}

export const intakeService = new IntakeService();
